import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePerms } from "@/context/PermContext";
import {
  LayoutDashboard, Users, Stethoscope, ClipboardList, BedDouble, Ambulance,
  CalendarClock, FlaskConical, Pill, Wallet, Scissors, FileText, Settings as SettingsIcon,
  Shield, IndianRupee, Files, ScrollText, Activity, LogOut, Search, ChevronRight, Monitor,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { LOGO_URL, VYOMRA_LOGO_URL, PLATFORM_NAME } from "@/lib/brand";
import { useBranding } from "@/context/BrandingContext";
import { MessageCircle, GraduationCap, Link2 } from "lucide-react";

const NAV = [
  { section: "OVERVIEW", items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
  ]},
  { section: "CLINICAL", items: [
    { to: "/patients", label: "Patients", icon: Users },
    { to: "/opd", label: "OPD", icon: ClipboardList },
    { to: "/ipd", label: "IPD", icon: BedDouble },
    { to: "/emergency", label: "Emergency", icon: Ambulance },
    { to: "/appointments", label: "Appointments", icon: CalendarClock },
    { to: "/investigations", label: "Investigations", icon: FlaskConical },
    { to: "/ot", label: "OT / Surgery", icon: Scissors },
    { to: "/beds", label: "Bed Management", icon: BedDouble },
  ]},
  { section: "STAFF", items: [
    { to: "/doctors", label: "Doctors", icon: Stethoscope },
    { to: "/commission", label: "Commission", icon: IndianRupee },
    { to: "/hrms", label: "HRMS", icon: Users },
  ]},
  { section: "FINANCE", items: [
    { to: "/billing", label: "Billing & Payments", icon: Wallet },
    { to: "/pharmacy", label: "Pharmacy", icon: Pill },
  ]},
  { section: "ENGAGE", items: [
    { to: "/whatsapp", label: "WhatsApp / SMS", icon: MessageCircle },
  ]},
  { section: "ADMIN", items: [
    { to: "/approvals", label: "Approvals", icon: Shield, mod: "billing" },
    { to: "/investigations-master", label: "Investigation Master", icon: FlaskConical, mod: "investigations" },
    { to: "/lab-master", label: "Lab Test Master", icon: FlaskConical, mod: "investigations" },
    { to: "/kiosk-settings", label: "Kiosk", icon: Monitor, mod: "settings" },
    { to: "/branding-settings", label: "Branding", icon: SettingsIcon, superAdminOnly: true },
    { to: "/pmjay", label: "PMJAY", icon: Shield, mod: "pmjay" },
    { to: "/abdm", label: "ABDM Center", icon: Activity, mod: "abdm", abdmOnly: true },
    { to: "/reports", label: "Reports", icon: FileText, mod: "reports" },
    { to: "/print-center", label: "Print Center", icon: Files, mod: "print_center" },
    { to: "/audit", label: "Audit Logs", icon: ScrollText, mod: "audit" },
    { to: "/users", label: "Users & Roles", icon: Users, mod: "user_management" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, mod: "settings" },
  ]},
];

// Nav items marked with `core: true` are internal/system pages and are never
// hidden by module-gating (Dashboard, Settings, Users, Kiosk, Print Center).
const NAV_WITH_MOD = [
  { section: "OVERVIEW", items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, core: true }]},
  { section: "CLINICAL", items: [
    { to: "/patients", label: "Patients", icon: Users, mod: "patients" },
    { to: "/opd", label: "OPD", icon: ClipboardList, mod: "opd" },
    { to: "/ipd", label: "IPD", icon: BedDouble, mod: "ipd" },
    { to: "/emergency", label: "Emergency", icon: Ambulance, mod: "emergency" },
    { to: "/appointments", label: "Appointments", icon: CalendarClock, mod: "appointments" },
    { to: "/investigations", label: "Investigations", icon: FlaskConical, mod: "investigations" },
    { to: "/walkin-investigations", label: "Walk-in / Reports", icon: FlaskConical, mod: "investigations" },
    { to: "/ot", label: "OT / Surgery", icon: Scissors, mod: "ot" },
    { to: "/beds", label: "Bed Management", icon: BedDouble, mod: "beds" },
  ]},
  { section: "STAFF", items: [
    { to: "/doctors", label: "Doctors", icon: Stethoscope, mod: "doctors" },
    { to: "/commission", label: "Commission", icon: IndianRupee, mod: "commission" },
    { to: "/hrms", label: "HRMS", icon: Users, mod: "hrms" },
  ]},
  { section: "FINANCE", items: [
    { to: "/billing", label: "Billing & Payments", icon: Wallet, mod: "billing" },
    { to: "/credits", label: "Credit Ledger", icon: Wallet, mod: "billing" },
    { to: "/debit", label: "Debit Transactions", icon: Wallet, mod: "billing" },
    { to: "/pharmacy", label: "Pharmacy", icon: Pill, mod: "pharmacy" },
  ]},
  { section: "ENGAGE", items: [
    { to: "/whatsapp", label: "WhatsApp / SMS", icon: MessageCircle, mod: "whatsapp" },
    { to: "/patient-link", label: "P-Link · Patient Link", icon: Link2, core: true, external: true, href: "https://kivihealth.com/doctor/login" },
  ]},
  { section: "ADMIN", items: [
    { to: "/approvals", label: "Approvals", icon: Shield, mod: "billing" },
    { to: "/investigations-master", label: "Investigation Master", icon: FlaskConical, mod: "investigations" },
    { to: "/lab-master", label: "Lab Test Master", icon: FlaskConical, mod: "investigations" },
    { to: "/reports/investigations/by-doctor", label: "Doctor-wise Investigations", icon: FlaskConical, mod: "reports" },
    { to: "/kiosk-settings", label: "Kiosk", icon: Monitor, core: true },
    { to: "/branding-settings", label: "Branding", icon: SettingsIcon, superAdminOnly: true },
    { to: "/subscription", label: "Subscription", icon: Wallet, adminOnly: true },
    { to: "/pmjay", label: "PMJAY", icon: Shield, mod: "pmjay" },
    { to: "/abdm", label: "ABDM Center", icon: Activity, mod: "abdm", abdmOnly: true },
    { to: "/reports", label: "Reports", icon: FileText, mod: "reports" },
    { to: "/print-center", label: "Print Center", icon: Files, core: true },
    { to: "/audit", label: "Audit Logs", icon: ScrollText, mod: "audit" },
    { to: "/users", label: "Users & Roles", icon: Users, core: true },
    { to: "/settings", label: "Settings", icon: SettingsIcon, core: true },
  ]},
  { section: "HELP", items: [
    { to: "/training", label: "Training / Help", icon: GraduationCap, core: true },
  ]},
];

export default function Layout({ children }) {
  const { user, logout, isModuleEnabled } = useAuth();
  const { can } = usePerms();
  const brand = useBranding();
  const location = useLocation();
  const nav = useNavigate();
  const [q, setQ] = useState("");

  const roleLabel = (user?.role || "").replace("_", " ").toUpperCase();
  // Filter nav by permissions AND hospital.enabled_modules.
  // Core system pages (dashboard, settings, users, kiosk, print-center) are
  // never hidden by module gating — they belong to every hospital.
  const visibleNav = NAV_WITH_MOD.map((sec) => ({
    ...sec,
    items: sec.items.filter((it) => {
      if (it.superAdminOnly) return user?.role === "super_admin";
      if (it.adminOnly) return ["admin", "super_admin"].includes(user?.role);
      if (it.abdmOnly && !brand.abdm_enabled) return false;
      if (it.core) return true;
      if (it.mod && !isModuleEnabled(it.mod)) return false;
      return !it.mod || can(it.mod, "view");
    }),
  })).filter((sec) => sec.items.length > 0);

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-60 bg-[#0B1120] text-slate-200 flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="px-3 py-3 border-b border-slate-800 bg-white">
          <div className="flex items-center justify-center h-16">
            <img src={VYOMRA_LOGO_URL} alt={PLATFORM_NAME} className="h-14 object-contain" />
          </div>
          <div className="text-center text-[9px] text-slate-500 uppercase tracking-[0.2em] mt-1">{PLATFORM_NAME} · {brand.short_name || "Hospital"}</div>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {visibleNav.map((sec) => (
            <div key={sec.section} className="mb-3">
              <div className="px-4 py-1 text-[10px] font-semibold tracking-[0.15em] text-slate-500">{sec.section}</div>
              {sec.items.map((it) => {
                const active = location.pathname === it.to || (it.to !== "/" && location.pathname.startsWith(it.to));
                const Icon = it.icon;
                const cls = `flex items-center gap-2 px-4 py-2 text-sm border-l-[3px] ${
                  active
                    ? "bg-blue-950/40 border-blue-500 text-white"
                    : "border-transparent text-slate-300 hover:bg-slate-800/40 hover:text-white"
                }`;
                // External nav items open in a new tab (e.g. P-Link → KiviHealth).
                if (it.external) {
                  return (
                    <a
                      key={it.to}
                      href={it.href || it.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`nav-${it.to.replace(/\//g, "-") || "home"}`}
                      className={cls}
                    >
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                      {it.label}
                    </a>
                  );
                }
                return (
                  <Link
                    key={it.to}
                    to={it.to}
                    data-testid={`nav-${it.to.replace(/\//g, "-") || "home"}`}
                    className={cls}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    {it.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-slate-800 text-xs text-slate-500">
          {PLATFORM_NAME} v1.0
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-slate-200 h-14 flex items-center gap-3 px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Divine Hospital</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-900 font-medium">{location.pathname === "/" ? "Dashboard" : location.pathname.split("/").filter(Boolean)[0]?.toUpperCase()}</span>
          </div>
          <form
            className="ml-auto relative"
            onSubmit={(e) => { e.preventDefault(); if (q.trim()) nav(`/patients?q=${encodeURIComponent(q)}`); }}
          >
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <Input
              data-testid="global-search-input"
              className="pl-8 h-9 w-72 border-slate-200"
              placeholder="Search patient / UHID / mobile"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </form>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-medium text-slate-900">{user?.name}</div>
              <div className="text-[10px] tracking-wider text-slate-500">{roleLabel}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </header>
        <div className="flex-1 p-6">{children}</div>
      </main>
    </div>
  );
}
